import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Cookie } from "lucide-react";

const KEY = "nf_cookie_consent";

export function getConsent() {
  try { return JSON.parse(localStorage.getItem(KEY)); } catch { return null; }
}

export function CookieConsent() {
  const [consent, setConsent] = useState(() => getConsent());
  const [manage, setManage] = useState(false);
  const [prefs, setPrefs] = useState({ functional: true, analytics: false });
  const { user } = useAuth();
  const { pathname } = useLocation();

  // Position the banner clear of any bottom-fixed CTA on mobile:
  // logged-in app pages have a bottom nav (+ optional action bar); logged-out
  // public opportunity/creator pages have a bottom action bar; elsewhere flat.
  const publicActionBar = /^\/(opportunity|creators?)\//.test(pathname);
  const bannerPos = user ? "nf-cookie-app" : publicActionBar ? "nf-cookie-actionbar" : "nf-cookie-flat";

  useEffect(() => {
    const existing = getConsent();
    if (existing?.prefs) setPrefs(existing.prefs);
  }, []);

  const save = (choice) => {
    const record = {
      choice,
      prefs: choice === "all" ? { functional: true, analytics: true }
        : choice === "reject" ? { functional: false, analytics: false }
        : prefs,
      at: new Date().toISOString(),
    };
    localStorage.setItem(KEY, JSON.stringify(record));
    setConsent(record);
    setManage(false);
  };

  if (consent && !manage) return null;

  return (
    <>
      {!consent && (
        <div data-testid="cookie-banner" className={`${bannerPos} fixed inset-x-0 z-[60] border-t border-zinc-200 bg-white/95 p-3 backdrop-blur-xl md:p-4`}>
          <div className="mx-auto flex max-w-5xl flex-col items-start gap-3 sm:flex-row sm:items-center">
            <div className="flex items-start gap-3">
              <Cookie className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600" />
              <p className="text-sm text-zinc-600">
                We use essential cookies to keep you signed in. With your consent we may also use optional cookies to improve Nearfluence.{" "}
                <Link to="/cookies" className="font-medium text-emerald-600 hover:underline">Learn more</Link>.
              </p>
            </div>
            <div className="flex shrink-0 flex-wrap gap-2 sm:ml-auto">
              <Button size="sm" variant="ghost" data-testid="cookie-manage" onClick={() => setManage(true)} className="rounded-full">Manage</Button>
              <Button size="sm" variant="outline" data-testid="cookie-reject" onClick={() => save("reject")} className="rounded-full">Reject non-essential</Button>
              <Button size="sm" data-testid="cookie-accept" onClick={() => save("all")} className="rounded-full bg-emerald-600 hover:bg-emerald-700">Accept all</Button>
            </div>
          </div>
        </div>
      )}

      <Dialog open={manage} onOpenChange={setManage}>
        <DialogContent data-testid="cookie-preferences" className="max-w-md">
          <DialogHeader>
            <DialogTitle>Cookie preferences</DialogTitle>
            <DialogDescription>Choose which optional cookies Nearfluence may use. Essential cookies are always on so the site works.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="flex items-center justify-between rounded-xl border border-zinc-200 p-3">
              <div><p className="text-sm font-semibold text-zinc-900">Essential</p><p className="text-xs text-zinc-500">Sign-in & security. Always active.</p></div>
              <Switch checked disabled />
            </div>
            <div className="flex items-center justify-between rounded-xl border border-zinc-200 p-3">
              <div><p className="text-sm font-semibold text-zinc-900">Functional</p><p className="text-xs text-zinc-500">Remembers your preferences.</p></div>
              <Switch data-testid="pref-functional" checked={prefs.functional} onCheckedChange={(v) => setPrefs((p) => ({ ...p, functional: v }))} />
            </div>
            <div className="flex items-center justify-between rounded-xl border border-zinc-200 p-3">
              <div><p className="text-sm font-semibold text-zinc-900">Analytics</p><p className="text-xs text-zinc-500">Helps us understand usage (off by default).</p></div>
              <Switch data-testid="pref-analytics" checked={prefs.analytics} onCheckedChange={(v) => setPrefs((p) => ({ ...p, analytics: v }))} />
            </div>
          </div>
          <Button data-testid="cookie-save-prefs" onClick={() => save("custom")} className="w-full rounded-full bg-emerald-600 hover:bg-emerald-700">Save preferences</Button>
        </DialogContent>
      </Dialog>
    </>
  );
}
